import Header from '../components/Header'
import Footer from '../components/Footer'
import { Link } from 'react-router-dom'

const SeoStrategy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-40"
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80"
              alt="SEO analytics dashboard"
            />
          </div>
          <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                <span className="block">Data-Driven SEO Strategies</span>
                <span className="block text-indigo-400">That Drive Sustainable Growth</span>
              </h1>
              <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-300">
                We develop customized SEO roadmaps that deliver consistent, measurable results and dominate your market's search landscape.
              </p>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-block bg-indigo-600 py-3 px-8 border border-transparent rounded-md text-base font-medium text-white hover:bg-indigo-700"
                >
                  Get Your Free SEO Audit
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { name: 'Organic Traffic Increase', value: '150-400%' },
                { name: 'Higher Conversion Rates', value: '3-5x' },
                { name: 'Cost Per Lead Reduction', value: '80%+' },
                { name: 'ROI Compared to PPC', value: '5.3x' },
              ].map((stat) => (
                <div key={stat.name} className="text-center p-6 bg-gray-50 rounded-lg shadow">
                  <p className="text-3xl font-bold text-indigo-600">{stat.value}</p>
                  <p className="mt-2 text-sm font-medium text-gray-500">{stat.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SEO Pillars Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Our SEO Framework</h2>
              <p className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                The 4 Pillars of Effective SEO
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Comprehensive strategies that address all ranking factors
              </p>
            </div>

            <div className="mt-16">
              <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-x-8 md:gap-y-10">
                {[
                  {
                    name: 'Technical SEO',
                    description: 'Foundation for search engine crawling and indexing',
                    features: ['Site architecture', 'Page speed optimization', 'Mobile responsiveness', 'Structured data']
                  },
                  {
                    name: 'Content Strategy',
                    description: 'Creating content that ranks and converts',
                    features: ['Keyword research', 'Content clusters', 'Search intent mapping', 'Content refresh']
                  },
                  {
                    name: 'On-Page SEO',
                    description: 'Optimizing individual pages for maximum visibility',
                    features: ['Title tags & meta', 'Header optimization', 'Internal linking', 'Image optimization']
                  },
                  {
                    name: 'Off-Page SEO',
                    description: 'Building authority and trust signals',
                    features: ['Link building', 'Local citations', 'Brand mentions', 'Digital PR']
                  }
                ].map((pillar) => (
                  <div key={pillar.name} className="relative bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex flex-col h-full">
                    <h3 className="text-lg font-medium text-gray-900">
                      {pillar.name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      {pillar.description}
                    </p>
                    <ul className="mt-4 space-y-2 flex-grow">
                      {pillar.features.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="flex-shrink-0 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="ml-2 text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      <a href="#" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        Learn more <span aria-hidden="true">→</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Our Process</h2>
              <p className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                A Strategic Approach to SEO Success
              </p>
            </div>

            <div className="mt-16">
              <div className="lg:grid lg:grid-cols-3 lg:gap-x-8">
                <div className="relative">
                  <div className="relative px-6 py-10 bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="absolute top-0 left-0 bg-indigo-500 h-1 w-full"></div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center bg-indigo-100 rounded-full h-16 w-16">
                        <span className="text-xl font-bold text-indigo-600">1</span>
                      </div>
                      <h3 className="mt-6 text-lg font-medium text-gray-900">Discovery & Audit</h3>
                      <p className="mt-2 text-base text-gray-500">
                        Comprehensive analysis of your current SEO performance and competitive landscape
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative mt-10 lg:mt-0">
                  <div className="relative px-6 py-10 bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="absolute top-0 left-0 bg-indigo-500 h-1 w-full"></div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center bg-indigo-100 rounded-full h-16 w-16">
                        <span className="text-xl font-bold text-indigo-600">2</span>
                      </div>
                      <h3 className="mt-6 text-lg font-medium text-gray-900">Strategy Development</h3>
                      <p className="mt-2 text-base text-gray-500">
                        Customized SEO roadmap with prioritized actions and KPIs
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative mt-10 lg:mt-0">
                  <div className="relative px-6 py-10 bg-white shadow-lg rounded-lg overflow-hidden">
                    <div className="absolute top-0 left-0 bg-indigo-500 h-1 w-full"></div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center bg-indigo-100 rounded-full h-16 w-16">
                        <span className="text-xl font-bold text-indigo-600">3</span>
                      </div>
                      <h3 className="mt-6 text-lg font-medium text-gray-900">Implementation & Growth</h3>
                      <p className="mt-2 text-base text-gray-500">
                        Ongoing optimization, reporting, and scaling of successful tactics
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Case Study Section */}
        <div id="audit" className="bg-indigo-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-8">
              <div className="mb-8 lg:mb-0">
                <h2 className="text-3xl font-extrabold text-white">
                  <span className="block">From #12 to #1</span>
                  <span className="block">in 6 Months</span>
                </h2>
                <p className="mt-4 text-lg text-indigo-200">
                  How we helped an eCommerce brand dominate their niche with strategic SEO, resulting in 428% more organic traffic.
                </p>
                <div className="mt-8">
                  <Link to="/case-studies" className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50">
                    View Case Study
                  </Link>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-12 w-12 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <blockquote>
                      <p className="text-lg text-gray-700">
                        "The SEO strategy completely transformed our business. We went from struggling to rank on page 2 to owning the top 3 positions for our most valuable keywords."
                      </p>
                      <footer className="mt-4">
                        <div className="flex items-center">
                          <div className="ml-4">
                            <div className="text-base font-medium text-gray-900">Jessica Williams</div>
                            <div className="text-base font-medium text-indigo-600">Marketing Director, EcoGear</div>
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Ready to Dominate Search Results?
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Get a customized SEO strategy that delivers consistent, high-quality traffic.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Get Started Today
                </Link>
              </div>
              <div className="ml-3 inline-flex">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                >
                  Request Free Audit
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default SeoStrategy
